import React, { useRef, useState } from 'react'

/** One action: a link out, or a button on the page. */
export interface ActionItem {
  icon: React.ReactElement
  label: string
  /** The brand colour that tints the icon and glows under the chip on hover. */
  color: string
  link?: string
  onClick?: () => void
  /** The page's one primary action: a solid chip among the glass ones. */
  primary?: boolean
  /** Asks for a look: a slowly turning hairline in the three colours, and the chip leans towards the pointer. */
  attention?: boolean
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup']
  'aria-expanded'?: boolean
}

interface ActionRowProps {
  items: ActionItem[]
  /** The primary chip's element, for giving focus back to it. */
  primaryRef?: React.Ref<HTMLButtonElement>
  onItemClick?: (label: string) => void
}

/**
 * The actions under the title as one family: pill chips of the same height and glass, icon
 * tinted in its brand colour with the label beside it. The primary one (About) is the solid
 * chip among them. On a phone the links shrink to round icon chips (labelled for assistive
 * tech) so the four fit one line, About keeping its word.
 */
export const ActionRow: React.FC<ActionRowProps> = ({ items, primaryRef, onItemClick }) => (
  <nav aria-label="Links" className="flex items-center justify-center gap-2.5 sm:gap-3">
    {items.map((item) => (
      <Chip key={item.label} item={item} primaryRef={item.primary ? primaryRef : undefined} onItemClick={onItemClick} />
    ))}
  </nav>
)

const Chip: React.FC<{
  item: ActionItem
  primaryRef?: React.Ref<HTMLButtonElement>
  onItemClick?: (label: string) => void
}> = ({ item, primaryRef, onItemClick }) => {
  const zone = useRef<HTMLSpanElement>(null)
  const [pull, setPull] = useState({ x: 0, y: 0 })
  const onMove = (e: React.PointerEvent) => {
    if (!item.attention || e.pointerType !== 'mouse' || !zone.current) return
    const r = zone.current.getBoundingClientRect()
    setPull({ x: (e.clientX - (r.left + r.width / 2)) * 0.25, y: (e.clientY - (r.top + r.height / 2)) * 0.25 })
  }
  const onLeave = () => setPull({ x: 0, y: 0 })
  const external = !!item.link && !item.link.startsWith('mailto:')
  const className = `group relative inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-full text-sm outline-none ${
    item.primary ? 'px-5' : 'w-11 px-0 sm:w-auto sm:px-4'
  } [-webkit-tap-highlight-color:transparent] transition-transform duration-[700ms] ease-out [transition-timing-function:var(--ease-spring)] hover:scale-[1.05] active:scale-95 active:duration-150 motion-reduce:transition-none ${
    item.primary ? 'bg-white text-black' : 'text-white/85'
  }`
  const transform = pull.x || pull.y ? `translate(${pull.x.toFixed(1)}px, ${pull.y.toFixed(1)}px)` : undefined
  const inner = (
    <>
      {/* The glow of the chip's colour under the glass, brighter on hover and focus. */}
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full opacity-25 blur-md transition-[opacity,transform] duration-[900ms] ease-out group-hover:opacity-70 group-hover:scale-110 group-focus-visible:opacity-70 motion-reduce:transition-none"
        style={{ background: item.color }}
      />
      {!item.primary && (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-[hsla(0,0%,100%,0.06)] backdrop-blur-md transition-[background-color] duration-[600ms] ease-out group-hover:bg-[hsla(0,0%,100%,0.12)] motion-reduce:transition-none"
          style={{ boxShadow: item.attention ? undefined : '0 0 0 1px hsla(0, 0%, 100%, 0.18) inset' }}
        />
      )}
      {item.attention && (
        <span aria-hidden="true" className="star-border absolute -inset-px rounded-full motion-reduce:animate-none" />
      )}
      <span aria-hidden="true" className="relative flex" style={{ color: item.primary ? undefined : item.color }}>
        {item.icon}
      </span>
      <span className={item.primary ? 'relative' : 'relative hidden sm:inline'}>{item.label}</span>
    </>
  )
  return (
    <span ref={zone} className="inline-block -m-4 p-4" onPointerMove={onMove} onPointerLeave={onLeave}>
      {item.link ? (
        <a
          href={item.link}
          // A mailto opens the mail app, not a tab: a new tab would stay blank where there is no mail app.
          target={external ? '_blank' : undefined}
          rel={external ? 'noreferrer' : undefined}
          aria-label={external ? `${item.label} (opens in a new tab)` : item.label}
          title={item.label}
          onClick={() => onItemClick?.(item.label)}
          className={`${className} focus-visible:ring-2 focus-visible:ring-white`}
          style={{ transform }}
        >
          {inner}
        </a>
      ) : (
        <button
          ref={primaryRef}
          type="button"
          onClick={item.onClick}
          aria-haspopup={item['aria-haspopup']}
          aria-expanded={item['aria-expanded']}
          className={`${className} focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black`}
          style={{ transform }}
        >
          {inner}
        </button>
      )}
    </span>
  )
}
